import { CHOICE, plural } from '../lib/labels.ts';
import { layout, VIEW } from '../lib/hemicycle.ts';
import type { Choice, Seat, VotingView } from '../lib/types.ts';

const COLOR: Record<Choice, string> = { for: 'var(--for)', against: 'var(--against)', abstain: 'var(--abstain)' };

type Props = {
  seats: Seat[];
  votes: Record<string, Choice>;
  voting: VotingView | null;
  present: number;
};

/** Escaños con su estado: ausente, presente, ya votó (secreta) o su voto (nominal). */
export function Hemicycle({ seats, votes, voting, present }: Props) {
  const { seats: positions, radius } = layout(seats.length);
  const secret = voting?.mode === 'secreta';
  const result = voting?.state === 'closed' ? voting.result : null;

  let big: string;
  let small: string;
  let tone = 'var(--ink)';
  if (result) {
    big = { approved: 'Aprobada', rejected: 'Rechazada', repeat: 'Empate', 'needs-casting': 'Empate', 'no-quorum': 'Sin quórum' }[result.status];
    tone = result.status === 'approved' ? 'var(--for)' : result.status === 'rejected' ? 'var(--against)' : 'var(--warn)';
    small = `${plural(voting!.ballotsCast, 'voto', 'votos')} de ${plural(present, 'presente', 'presentes')}`;
  } else if (voting) {
    big = `${voting.ballotsCast}/${present}`;
    small = voting.state === 'voting' ? 'han votado' : 'empate: voto de calidad';
  } else {
    big = String(present);
    small = `${present === 1 ? 'presente' : 'presentes'} de ${seats.length}`;
  }

  return (
    <div class="stage">
      <svg viewBox={`0 0 ${VIEW.width} ${VIEW.height}`} role="img" aria-label={`Hemiciclo: ${big} ${small}`}>
        {seats.map((seat, i) => {
          const p = positions[i]!;
          const vote = votes[seat.memberId];
          let fill = 'var(--surface2)';
          let stroke = 'var(--muted)';
          let title = seat.displayName;
          if (!seat.present) {
            fill = 'none';
            stroke = 'var(--line)';
            title += ', ausente';
          } else if (vote) {
            fill = stroke = COLOR[vote];
            title += `, ${CHOICE[vote].toLowerCase()}`;
          } else if (secret && voting && voting.ballotsCast > 0) {
            title += ', presente';
          } else {
            title += voting ? ', sin votar' : ', presente';
          }
          return (
            <circle key={seat.memberId} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={radius.toFixed(1)} fill={fill} stroke={stroke}
              stroke-width="1.5" stroke-dasharray={seat.present ? undefined : '2.5 2.5'}>
              <title>{title}</title>
            </circle>
          );
        })}
        <text x={VIEW.cx} y="272" text-anchor="middle" font-family="var(--f-display)" font-weight="700" font-size="30" fill={tone}>{big}</text>
        <text x={VIEW.cx} y="294" text-anchor="middle" font-size="14" fill="var(--muted)">{small}</text>
      </svg>
      <div class="legend">
        {secret
          ? <span>Votación secreta: el hemiciclo no muestra quién votó qué.</span>
          : (['for', 'against', 'abstain'] as const).map((c) => <span key={c}><i class="sw" style={{ background: COLOR[c] }} />{CHOICE[c]}</span>)}
        <span><i class="sw" style={{ borderColor: 'var(--muted)', background: 'var(--surface2)' }} />Presente</span>
        <span><i class="sw" style={{ border: '1.5px dashed var(--muted)' }} />Ausente</span>
      </div>
    </div>
  );
}
